import { Injectable } from '@angular/core';
import {IConfigSharedHotel} from "../../hotel/models/config-shared-hotel";
import {ICurrencies} from "../resources/hotel/ISearchResponse";
import {ICost} from "../resources/model/search/IBooking";
import {HotelCurrencyService} from "../hotel-currency/hotel-currency.service";
import {QuotationService} from "../../hotel/services/quotation/quotation.service";
import intersection from "lodash-es/intersection";
import pick from "lodash-es/pick";
import {IOriginalCurrency, IPriceDetail, IPriceDetails} from "../resources/booking/interfaces/IGetBookings";

export interface ICurrencyConfig {
  mainCode: string;
  asideCode: string;
}

export interface IPrice {
  amount: number;
  currency: string;
}

export interface IMainAndAsidePrices {
  mainPrice: IPrice;
  asidePrice: IPrice;
}

export interface IMainAndAsidePricesDetail {
  mainPrice: IPriceDetail;
  asidePrice?: IOriginalCurrency;
}

@Injectable()
export class GetMainCurrencyService {

  constructor(
      private hotelCurrencyService: HotelCurrencyService,
      private quotationService: QuotationService
  ){}

  private hasAsidePrice(cost: ICost, currencies: ICurrencies, currencyConfig: IConfigSharedHotel) : boolean {
    const showProviderCurrency : boolean = this.hotelCurrencyService.showAsideCurrency(currencyConfig);

    const costCurrencies: string[] = cost ? Object.keys(cost || {}) : [];

    const providerCurrencyExists : boolean = costCurrencies.filter((currencyCode : string) => currencyCode !== 'EUR').length > 0;
    const userCurrencyExists : boolean = typeof costCurrencies.find((currencyCode : string) => currencyCode === 'EUR') !== 'undefined';
    const currencyConfigExists: boolean = intersection(costCurrencies, Object.keys(currencies || {})).length >= 2;

    return providerCurrencyExists && userCurrencyExists && showProviderCurrency && currencyConfigExists;
  }

  public getCurrencyConfig(cost: ICost, currencies: ICurrencies, currencyConfig: IConfigSharedHotel) : ICurrencyConfig{
    const currenciesIntersection: string[] = intersection(Object.keys(cost || {}), Object.keys(currencies || {}));
    const availableCurrencies: ICurrencies = pick(currencies, currenciesIntersection);
    const availableCosts: ICost = pick(cost, currenciesIntersection);

    const mainCode : string = this.hotelCurrencyService.getCurrencyString(
        availableCurrencies,
        currencyConfig
    );

    const asideCode : string = this.getAsideCode(cost, currencies, currencyConfig, availableCosts);

    return {
      mainCode,
      asideCode
    };
  }

  private getAsideCode(cost: ICost, currencies: ICurrencies, currencyConfig: IConfigSharedHotel, availableCosts: ICost): string{
    if (this.hasAsidePrice(cost, currencies, currencyConfig)){
      if (this.hotelCurrencyService.isProviderMainCurrency(currencyConfig)){
        return "EUR";
      }
      return this.hotelCurrencyService.getProviderCurrencyString(availableCosts);
    }

    return null;
  }

  public getMainAndAsidePrice(cost: ICost, currencies: ICurrencies, config: ICurrencyConfig): IMainAndAsidePrices {
    let mainPrice: IPrice = null;
    let asidePrice: IPrice = null;
    if (config && config.mainCode) {
      mainPrice = {
        amount: this.quotationService.getPrice(cost, currencies[config.mainCode]),
        currency: config.mainCode,
      };
    }

    if (config && config.asideCode) {
      asidePrice = {
        amount: this.quotationService.getPrice(cost, currencies[config.asideCode]),
        currency: config.asideCode,
      };
    }

    return {
      mainPrice,
      asidePrice
    };
  }
}
